import React from 'react';
import { useTheme } from 'styled-components';
import { useTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';
import { Box, Button, ImagePanel } from 'edenred-ui';

import SetUpAccountImg from '@epi-assets/images/SetUpAccount.jpg';
import { ICustomerData } from '@epi-models/pages/Home';
import { OnboardingSupportInformation } from '@epi-components/OnboardingSupportInformation';
import OnboardingStep from '@epi-components/OnboardingStepper/components/OnboardingStep/OnboardingStep';
import { routerPaths } from '@epi-constants/routerPaths';
import { goToPage } from '@epi-actions/navigation';
import { useOnboardingWizard } from '@epi-shared/hooks';

import { HomeNotification } from '../HomeNotification';

import { BoxWithShadow } from './NewCustomer.styles';

export const NewCustomer = ({ data }: { data: ICustomerData }) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const { handleGoOnboardingPayment } = useOnboardingWizard();
  const iconColumnWidth = '45px';
  const {
    gray0,
    primaryBackgroundColor,
    sizeExtraExtraLarge,
    fontSemiBold,
    sizeLarge
  } = useTheme();
  const { isBenefitsSet, isEmployeesSet, isMoneyTransfered, isLoadInitiated } =
    data;
  const loadDone = isMoneyTransfered || isLoadInitiated;
  const isSelectBenefitActive = !isBenefitsSet;
  const isAddEmployeesActive = isBenefitsSet && !isEmployeesSet;
  const isLoadBalanceActive = isBenefitsSet && isEmployeesSet && !loadDone;

  const handleStart = () => {
    if (!isBenefitsSet) {
      dispatch(goToPage(routerPaths.manageBenefits));
    } else if (!isEmployeesSet) {
      dispatch(goToPage(routerPaths.beneficiaries));
    } else if (!loadDone) {
      handleGoOnboardingPayment();
    }
  };

  return (
    <Box display="flex" justifyContent="center">
      <HomeNotification />
      <Box minHeight={328} pt={5} mx={2.5} maxWidth={1336}>
        <Box
          display={'flex'}
          gap={0.5}
          alignItems="center"
          maxWidth={'1375px'}
          margin={'0 auto'}
          pb={5}
          pl={5}
          pr={5}
          fontSize={sizeExtraExtraLarge}
          fontWeight={fontSemiBold}
        >
          {t('home.set_up_account.title')}
        </Box>
        <Box pb={5}>
          <ImagePanel
            imageSrc={SetUpAccountImg}
            reverse
            dismissId="set-up-account-dismiss"
          >
            <BoxWithShadow height="100%" p={5} bgcolor={gray0}>
              <Box mt={4} mb={3}>
                <Box display="flex" flexDirection="column" gap={5}>
                  <Box display="flex">
                    <Box flexShrink={0} width={iconColumnWidth}>
                      <OnboardingStep
                        stepNumber={1}
                        isDone={isBenefitsSet}
                        isActive={isSelectBenefitActive}
                      />
                    </Box>
                    <Box pl={1}>
                      <Box fontSize={sizeLarge} fontWeight={fontSemiBold}>
                        {t('home.set_up_account.select_benefits_title')}
                      </Box>
                      <Box pt={2}>
                        {t('home.set_up_account.select_benefits_description')}
                      </Box>
                    </Box>
                  </Box>

                  <Box display="flex">
                    <Box flexShrink={0} width={iconColumnWidth}>
                      <OnboardingStep
                        stepNumber={2}
                        isDone={isEmployeesSet}
                        isActive={isAddEmployeesActive}
                      />
                    </Box>
                    <Box pl={1}>
                      <Box fontSize={sizeLarge} fontWeight={fontSemiBold}>
                        {t('home.set_up_account.add_employees_title')}
                      </Box>
                      <Box pt={2}>
                        {t('home.set_up_account.add_employees_description')}
                      </Box>
                    </Box>
                  </Box>

                  <Box display="flex">
                    <Box flexShrink={0} width={iconColumnWidth}>
                      <OnboardingStep
                        stepNumber={3}
                        isActive={isLoadBalanceActive}
                        isDone={isMoneyTransfered || isLoadInitiated}
                      />
                    </Box>
                    <Box pl={1}>
                      <Box fontSize={sizeLarge} fontWeight={fontSemiBold}>
                        {t('home.set_up_account.load_balance_title')}
                      </Box>
                      <Box pt={2}>
                        {t('home.set_up_account.load_balance_description')}
                      </Box>
                    </Box>
                  </Box>
                </Box>
              </Box>
            </BoxWithShadow>
          </ImagePanel>

          <Box
            bgcolor={primaryBackgroundColor}
            display="flex"
            justifyContent="end"
            py={2}
            px={5}
          >
            <Button onClick={handleStart} id={'onboarding-'}>
              {isBenefitsSet
                ? t('home.set_up_account.continue')
                : t('home.set_up_account.start_button')}
            </Button>
          </Box>
        </Box>
        <OnboardingSupportInformation />
      </Box>
    </Box>
  );
};
